// Dashboard-Widget — 3 KPI-Zahlen für Beistelteile/Wareneingang
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Package, AlertTriangle } from 'lucide-react';
import { api, type WareneingangUebersicht } from '../lib/api';

export default function WareneingangWidget() {
  const [data, setData] = useState<WareneingangUebersicht | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.wareneingang.uebersicht();
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  const hasUeberfaellig = (data?.ueberfaellig ?? 0) > 0;

  return (
    <Link
      to="/wareneingang"
      className={`block bg-white rounded-xl border p-4 hover:border-blue-500 transition-colors ${
        hasUeberfaellig ? 'border-red-300' : 'border-slate-200'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Package className="w-4 h-4 text-blue-600" />
          Wareneingang
        </h3>
        {hasUeberfaellig && <AlertTriangle className="w-4 h-4 text-red-600" />}
      </div>
      {error ? (
        <div className="text-xs text-red-600">{error}</div>
      ) : !data ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <div key={i} className="h-6 bg-slate-100 rounded animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 text-center">
          <Stat label="Erwartet" value={data.erwartet_woche} color="text-amber-600" />
          <Stat label="Heute" value={data.heute_eingegangen} color="text-emerald-600" />
          <Stat label="Überfällig" value={data.ueberfaellig} color={data.ueberfaellig > 0 ? "text-red-600" : "text-slate-400"} />
        </div>
      )}
    </Link>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}
